import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  cancelExternalSignup,
  completeExternalSignup,
  fetchAccountLegalDocuments,
  fetchExternalSignupDraft,
  startExternalSignupEmail,
  verifyExternalSignupEmail,
  type LegalDocumentsDescriptor,
  type ExternalSignupDraftResponse,
} from "@/api/auth";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/components/layout/AuthShell";

const EMAIL_PATTERN = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/iu;
const inFlightVerifications = new Map<string, ReturnType<typeof verifyExternalSignupEmail>>();

function verifyOnce(token: string) {
  const current = inFlightVerifications.get(token);
  if (current) return current;
  const pending = verifyExternalSignupEmail(token);
  inFlightVerifications.set(token, pending);
  const clear = () => {
    if (inFlightVerifications.get(token) === pending) inFlightVerifications.delete(token);
  };
  void pending.then(clear, clear);
  return pending;
}

export function ExternalSignupPage() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const [draft, setDraft] = useState<ExternalSignupDraftResponse | null>(null);
  const [terms, setTerms] = useState<LegalDocumentsDescriptor | null>(null);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [mailSent, setMailSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [completionState, setCompletionState] = useState<"idle" | "login_required" | "result_unknown">("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    void Promise.all([fetchExternalSignupDraft(), fetchAccountLegalDocuments()]).then(
      ([currentDraft, currentTerms]) => {
        setDraft(currentDraft);
        setTerms(currentTerms);
        if (currentDraft.accountEmailHint) setEmail(currentDraft.accountEmailHint);
        if (currentDraft.fullNameHint) setFullName(currentDraft.fullNameHint);
      },
      () => setFailed(true),
    );
  }, []);

  const refreshEmailStatus = async () => {
    setBusy(true);
    setError("");
    try {
      const current = await fetchExternalSignupDraft();
      setDraft(current);
      if (current.accountEmailStatus !== "verified") setError("メールはまだ確認されていません。メール内のリンクで確認を完了してください。");
    } catch {
      setError("確認状況を取得できませんでした。もう一度お試しください。");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (!mailSent) return;
    const refresh = () => { void fetchExternalSignupDraft().then(setDraft, () => undefined); };
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [mailSent]);

  const cancel = async () => {
    setBusy(true);
    setError("");
    try {
      await cancelExternalSignup();
      navigate("/login", { replace: true });
    } catch {
      setError("登録を中断できませんでした。もう一度お試しください。");
    } finally {
      setBusy(false);
    }
  };
  const cancelButton = <Button type="button" variant="outline" disabled={busy} onClick={() => void cancel()}>登録を中止してログインへ戻る</Button>;

  const startEmailVerification = async (event: FormEvent) => {
    event.preventDefault();
    const normalized = email.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(normalized) || normalized.length > 254) {
      setError("有効なメールアドレスを入力してください。");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await startExternalSignupEmail(normalized);
      setMailSent(true);
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 401) setFailed(true);
      else setError("確認メールを送信できませんでした。時間をおいて再度お試しください。");
    } finally {
      setBusy(false);
    }
  };

  const complete = async (event: FormEvent) => {
    event.preventDefault();
    if (!fullName.trim() || [...fullName.trim()].length > 200 || !accepted || !terms) {
      setError("氏名を入力し、約款・ポリシーを確認して同意してください。");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await completeExternalSignup({
        fullName: fullName.trim(),
        legalDocumentsAccepted: true,
        termsAndPoliciesVersion: terms.termsAndPolicies.version, serviceTermsVersion: terms.serviceTerms.version, cookiePolicyVersion: terms.cookiePolicy.version,
      });
      try {
        await refreshMe();
        navigate("/console/dashboard", { replace: true });
      } catch {
        setCompletionState("login_required");
      }
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 401) {
        setFailed(true);
      } else if (cause instanceof ApiError && cause.code === "legal_documents_changed") {
        try {
          setTerms(await fetchAccountLegalDocuments());
          setAccepted(false);
          setError("約款・ポリシーが更新されました。最新版を確認して、もう一度同意してください。");
        } catch {
          setCompletionState("result_unknown");
        }
      } else if (cause instanceof ApiError && cause.code === "auth_session_unavailable") {
        setCompletionState("login_required");
      } else if (!(cause instanceof ApiError) || cause.code === "auth_result_unknown") {
        setCompletionState("result_unknown");
      } else {
        setError("登録を完了できませんでした。外部アカウントまたはアカウントメールは既に使用されている可能性があります。自動連携は行いません。ログインを試すか、登録からやり直してください。");
      }
    } finally {
      setBusy(false);
    }
  };

  if (failed) return <AuthShell title="登録を継続できません" description="登録情報の有効期限が切れたか、利用できなくなりました。登録画面でGoogleまたはMicrosoftの本人確認からやり直してください。"><Link className="text-primary underline" to="/signup">登録をやり直す</Link><div className="mt-4">{error ? <p role="alert">{error}</p> : null}{cancelButton}</div></AuthShell>;
  if (completionState === "login_required") return <AuthShell title="アカウント登録は完了しました" description="セッションを開始できなかったため、登録済みの外部アカウントでログインしてください。"><Link className="text-primary underline" to="/login">ログインへ進む</Link></AuthShell>;
  if (completionState === "result_unknown") return <AuthShell title="登録結果を確認できません" description="重複登録を防ぐため、この画面から再送しません。まず登録済みの外部アカウントでログインを試してください。"><Link className="text-primary underline" to="/login">ログインへ進む</Link></AuthShell>;
  if (!draft || !terms) return <AuthShell title="登録情報を確認しています" description="この画面を閉じずにお待ちください。"><p role="status">確認中…</p></AuthShell>;

  if (draft.accountEmailStatus === "required") {
    if (draft.provider !== "microsoft") {
      return <AuthShell title="登録を継続できません" description="Googleから検証済みメールを確認できませんでした。"><Link className="text-primary underline" to="/signup">登録からやり直す</Link></AuthShell>;
    }
    return <AuthShell title="アカウントメールを確認" description="外部アカウントのメールとは独立して、EXTONE Driveからの通知とローカル認証復旧に使うメールを確認します。">
      {error ? <div role="alert" className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div> : null}
      {mailSent
        ? <div className="space-y-4"><p role="status" className="rounded-md border bg-muted p-4 text-sm">入力された宛先へ確認手順を送信しました。メールに記載された有効期限までにリンクを開いてください。別のブラウザでも確認できます。迷惑メールフォルダーも確認してください。</p><p className="text-sm text-muted-foreground">届かない場合は送信先を確認して再送できます。登録情報の期限が切れた場合は、外部アカウントの本人確認からやり直してください。</p><Button variant="outline" disabled={busy} onClick={() => void refreshEmailStatus()}>メールの確認状況を更新</Button><Button variant="outline" disabled={busy} onClick={() => setMailSent(false)}>送信先を確認して再送する</Button></div>
        : <form className="space-y-4" onSubmit={startEmailVerification}><div className="space-y-2"><Label htmlFor="external-signup-email">アカウントメール</Label><Input id="external-signup-email" type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} disabled={busy} required /></div><Button className="w-full" disabled={busy}>確認メールを送信</Button></form>}
      <div className="mt-4">{cancelButton}</div>
    </AuthShell>;
  }

  return <AuthShell title="アカウント登録を完了" description={`${draft.provider === "google" ? "Google" : "Microsoft"}をログイン方法として登録し、確認済みアカウントメールを設定します。`}>
    <form className="space-y-4" onSubmit={complete}>
      {error ? <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div> : null}
      <div className="rounded-md border bg-muted p-3 text-sm"><span className="font-medium">アカウントメール:</span> {draft.email}</div>
      <p className="text-xs text-muted-foreground">このメールは通知とローカル認証復旧に使います。Google / Microsoftのログイン識別子とは独立しており、後から変更できます。</p>
      <div className="space-y-2"><Label htmlFor="external-signup-name">氏名</Label><Input id="external-signup-name" autoComplete="name" value={fullName} onChange={(event) => setFullName(event.target.value)} disabled={busy} /></div>
      <label className="flex items-start gap-3 rounded-md border p-3 text-sm"><input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} disabled={busy} className="mt-1" /><span><a href={terms.termsAndPolicies.url} target="_blank" rel="noreferrer" className="text-primary underline">約款・ポリシー一覧</a>を確認し、<a href={terms.serviceTerms.url} target="_blank" rel="noreferrer" className="text-primary underline">利用約款</a>と<a href={terms.cookiePolicy.url} target="_blank" rel="noreferrer" className="text-primary underline">Cookieポリシー</a>に同意します。</span></label>
      <Button className="w-full" disabled={busy}>登録を完了</Button>
    </form>
    <div className="mt-4">{cancelButton}</div>
  </AuthShell>;
}

export function ExternalSignupEmailVerifyPage() {
  const navigate = useNavigate();
  const started = useRef(false);
  const token = useRef("");
  const [phase, setPhase] = useState<"checking" | "confirm" | "verified" | "failed">("checking");

  const showResult = useCallback((result: Awaited<ReturnType<typeof verifyExternalSignupEmail>>) => {
    if (result.status === "confirmation_required") setPhase("confirm");
    else if (result.canContinueRegistration) navigate("/signup/external", { replace: true });
    else setPhase("verified");
  }, [navigate]);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    token.current = new URLSearchParams(globalThis.location.hash.slice(1)).get("token") ?? "";
    if (!token.current) { setPhase("failed"); return; }
    void verifyOnce(token.current).then(showResult, () => setPhase("failed"));
  }, [showResult]);

  const confirm = async () => {
    setPhase("checking");
    try { showResult(await verifyExternalSignupEmail(token.current, true)); }
    catch { setPhase("failed"); }
  };
  if (phase === "confirm") return <AuthShell title="アカウント登録の確認" description="このアカウント登録を開始したのはあなたですか？心当たりがある場合は、メール認証を完了してください。"><Button onClick={() => void confirm()}>メール認証を完了</Button></AuthShell>;
  if (phase === "verified") return <AuthShell title="メール認証が完了しました" description="登録を始めたブラウザの画面に戻って登録を続けてください。画面が切り替わらない場合は「メールの確認状況を更新」を押してください。">{null}</AuthShell>;
  if (phase === "failed") return <AuthShell title="確認リンクを利用できません" description="リンクの期限が切れたか、再送・登録中止によって無効になりました。登録を始めたブラウザで確認メールを再送してください。登録情報も期限切れの場合は、登録をやり直してください。"><Link className="text-primary underline" to="/signup/external">登録画面へ戻る</Link></AuthShell>;
  return <AuthShell title="アカウントメールを確認しています" description="この画面を閉じずにお待ちください。"><p role="status">確認中…</p></AuthShell>;
}
